import { DISABLED_SHAPE, FOCUS_RING } from './states';

/**
 * Case à cocher (US-25), en remplacement des cases natives.
 *
 * Le constat : 53 cases natives de 13 × 13 px, sur un écran dont le §4 de
 * CLAUDE.md dit qu'il doit s'utiliser « avec les mains grasses ». Une case
 * native ne se redimensionne pas de façon fiable d'un navigateur à l'autre,
 * et `transform: scale()` agrandit le dessin sans agrandir la zone de frappe.
 *
 * D'où un `<button role="checkbox">` : la **zone de frappe** fait au moins
 * 44 px (48 px en mode cuisine) et la **marque visuelle**, elle, garde la
 * taille qui convient à la densité de l'écran. Les deux dimensions sont
 * séparées, ce qu'une case native ne permet pas.
 *
 * `role="checkbox"` + `aria-checked` : un lecteur d'écran l'annonce comme une
 * case à cocher, et la barre d'espace la bascule — comportement natif d'un
 * `<button>`, qu'il n'y a donc pas à réimplémenter.
 */
export type CheckboxSize = 'compact' | 'default' | 'cook';

/** Taille de la zone de frappe et de la marque, séparément. */
const SIZE_CLASSES: Record<CheckboxSize, { hit: string; mark: string }> = {
  // 44 px : plancher général (US-25). En dessous, on rate la case une fois sur trois.
  compact: { hit: 'size-touch-min', mark: 'size-5' },
  default: { hit: 'size-touch', mark: 'size-5' },
  // Mode cuisine : la marque grossit avec la cible, on coche sans regarder.
  cook: { hit: 'size-touch', mark: 'size-7' },
};

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Libellé accessible. Obligatoire : une case sans nom ne s'annonce pas. */
  label: string;
  size?: CheckboxSize;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function Checkbox({
  checked,
  onChange,
  label,
  size = 'default',
  disabled = false,
  className = '',
  'data-testid': testId,
}: CheckboxProps): React.JSX.Element {
  const { hit, mark } = SIZE_CLASSES[size];
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => { onChange(!checked); }}
      className={`inline-flex shrink-0 items-center justify-center rounded-control transition ${DISABLED_SHAPE} ${hit} ${FOCUS_RING} ${className}`.trim()}
    >
      <span
        aria-hidden="true"
        className={`flex items-center justify-center rounded-[0.25rem] border-2 transition-colors ${mark} ${
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border-interactive bg-transparent'
        }`}
      >
        {checked && (
          // Coche dessinée plutôt qu'un caractère « ✓ » : une police peut ne
          // pas l'avoir, et sa taille varierait avec la marque.
          <svg viewBox="0 0 16 16" className="size-3/4" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M3 8.5 6.5 12 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </button>
  );
}
